import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { UserSelector } from '@tix/auth/state';
import { map, take, distinctUntilChanged } from 'rxjs/operators';

import {
  USER_PREFERENCE_KEYS,
  UserPreferences,
  USER_PREFERENCES_STORAGE_KEY
} from './user-preferences.model';

interface StoredUserPreferences {
  userId: string;
  preferences: UserPreferences;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class TixUserPreferencesService {
  // Internal state
  private preferencesSubject = new BehaviorSubject<UserPreferences>({});

  // Public observables
  public readonly preferences$ = this.preferencesSubject.asObservable();

  // Current user ID
  private currentUserId$ = this.store
    .select(UserSelector.getAuthenticatedUser)
    .pipe(
      map(user => user?.uid),
      distinctUntilChanged()
    );

  // Track the last known user to prevent unnecessary reloads
  private lastKnownUserId: string | undefined;

  constructor(private store: Store) {
    // Subscribe to user changes and load preferences accordingly
    this.currentUserId$.subscribe(userId => {
      // Only process if the user actually changed
      if (this.lastKnownUserId !== userId) {
        console.log(`User changed from ${this.lastKnownUserId} to ${userId}`);

        if (userId) {
          // User logged in - load their preferences
          this.loadPreferencesForUser(userId);
        }
        // Note: When user logs out (userId becomes null/undefined),
        // we DON'T clear preferences anymore - they stay in memory
        // until the next user logs in

        this.lastKnownUserId = userId;
      }
    });
  }

  /**
   * Get a specific preference value
   */
  getPreference(key: USER_PREFERENCE_KEYS): string | undefined {
    const preferences = this.preferencesSubject.value;
    return preferences[key];
  }

  /**
   * Set a specific preference value
   */
  setPreference(key: USER_PREFERENCE_KEYS, value: string): void {
    this.currentUserId$.pipe(take(1)).subscribe(userId => {
      if (!userId) {
        console.warn('Cannot save preferences: User not authenticated');
        return;
      }

      const currentPreferences = this.preferencesSubject.value;
      const updatedPreferences = {
        ...currentPreferences,
        [key]: value
      };

      this.preferencesSubject.next(updatedPreferences);
      this.savePreferencesForUser(userId, updatedPreferences);
    });
  }

  /**
   * Remove a specific preference key (but save the updated preferences)
   */
  removePreference(key: USER_PREFERENCE_KEYS): void {
    this.currentUserId$.pipe(take(1)).subscribe(userId => {
      if (!userId) {
        console.warn('Cannot remove preferences: User not authenticated');
        return;
      }

      const currentPreferences = this.preferencesSubject.value;
      const updatedPreferences = { ...currentPreferences };
      delete updatedPreferences[key];

      this.preferencesSubject.next(updatedPreferences);
      this.savePreferencesForUser(userId, updatedPreferences);
    });
  }

  /**
   * Get all preferences
   */
  getAllPreferences(): UserPreferences {
    return this.preferencesSubject.value;
  }

  /**
   * Update multiple preferences at once
   */
  updatePreferences(preferences: Partial<UserPreferences>): void {
    this.currentUserId$.pipe(take(1)).subscribe(userId => {
      if (!userId) {
        console.warn('Cannot update preferences: User not authenticated');
        return;
      }

      const currentPreferences = this.preferencesSubject.value;
      const updatedPreferences = {
        ...currentPreferences,
        ...preferences
      };

      this.preferencesSubject.next(updatedPreferences);
      this.savePreferencesForUser(userId, updatedPreferences);
    });
  }

  /**
   * Reset preferences to default values for current user
   * Note: This saves default values to localStorage (doesn't delete)
   */
  resetToDefaults(): void {
    this.currentUserId$.pipe(take(1)).subscribe(userId => {
      if (!userId) {
        console.warn('Cannot reset preferences: User not authenticated');
        return;
      }

      // Reset to empty preferences (default state) but still save them
      const defaultPreferences: UserPreferences = {};

      this.preferencesSubject.next(defaultPreferences);
      this.savePreferencesForUser(userId, defaultPreferences);
      console.log(`Reset preferences to defaults for user: ${userId}`);
    });
  }

  /**
   * Get current user ID (for debugging purposes)
   */
  getCurrentUserId(): Observable<string | undefined> {
    return this.currentUserId$;
  }

  /**
   * Check if user is authenticated and can save preferences
   */
  isUserAuthenticated(): Observable<boolean> {
    return this.currentUserId$.pipe(map(userId => !!userId));
  }

  /**
   * Generate user-specific storage key
   */
  private getUserStorageKey(userId: string): string {
    return `${USER_PREFERENCES_STORAGE_KEY}_${userId}`;
  }

  /**
   * Load preferences for a specific user
   * This method NEVER clears preferences - it either loads them or preserves existing ones
   */
  private loadPreferencesForUser(userId: string): void {
    try {
      const userStorageKey = this.getUserStorageKey(userId);
      const stored = localStorage.getItem(userStorageKey);

      if (stored) {
        const storedData: StoredUserPreferences = JSON.parse(stored);

        // Validate the stored data
        if (
          storedData &&
          storedData.preferences &&
          typeof storedData.preferences === 'object'
        ) {
          this.preferencesSubject.next(storedData.preferences);
          console.log(
            `Successfully loaded preferences for user: ${userId}`,
            storedData.preferences
          );
        } else {
          console.warn(
            `Invalid preference data for user: ${userId}, initializing with defaults`
          );
          this.initializePreferencesForUser(userId);
        }
      } else {
        // No stored preferences found - initialize with empty but save immediately
        console.log(
          `No stored preferences found for user: ${userId}, initializing new preferences`
        );
        this.initializePreferencesForUser(userId);
      }
    } catch (error) {
      console.error('Error loading user preferences:', error);
      // On error, don't change current preferences at all
      // Just try to save current state for the user
      console.log(
        'Attempting to preserve current preferences due to load error'
      );
      const currentPreferences = this.preferencesSubject.value;
      this.savePreferencesForUser(userId, currentPreferences);
    }
  }

  /**
   * Initialize preferences for a new user
   */
  private initializePreferencesForUser(userId: string): void {
    const initialPreferences: UserPreferences = {};
    this.preferencesSubject.next(initialPreferences);
    this.savePreferencesForUser(userId, initialPreferences);
    console.log(`Initialized empty preferences for user: ${userId}`);
  }

  /**
   * Save preferences for a specific user to localStorage
   * This method has multiple safeguards to ensure data is never lost
   */
  private savePreferencesForUser(
    userId: string,
    preferences: UserPreferences
  ): void {
    try {
      // Validate inputs
      if (!userId || typeof userId !== 'string') {
        console.error('Cannot save preferences: Invalid user ID');
        return;
      }

      if (!preferences || typeof preferences !== 'object') {
        console.error('Cannot save preferences: Invalid preferences object');
        return;
      }

      const userStorageKey = this.getUserStorageKey(userId);
      const dataToStore: StoredUserPreferences = {
        userId,
        preferences: { ...preferences }, // Create a copy to ensure immutability
        timestamp: Date.now()
      };

      // Attempt to save to localStorage
      localStorage.setItem(userStorageKey, JSON.stringify(dataToStore));
      console.log(
        `Successfully saved preferences for user: ${userId}`,
        preferences
      );

      // Verify the save was successful by reading it back
      const verification = localStorage.getItem(userStorageKey);
      if (!verification) {
        console.error(
          'Preference save verification failed - data not found in localStorage'
        );
      }
    } catch (error) {
      console.error('Critical error saving user preferences:', error);
      // Even if save fails, don't modify the in-memory preferences
      // This preserves the user's settings for the current session
    }
  }
}
